import { useMutation } from '@tanstack/react-query';
import { SovosFileUpload, SovosLink } from 'mosaic-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { uploadFile } from 'src/api/locationMappings';
import FormStepLayout from 'src/components/FormStepLayout';
import type { SovosUploadFile } from 'src/interfaces/SovosUploadFile';
import type {
	LocationMappingEditorFormState,
	LocationMappingEditorFormStep,
} from 'src/pages/locationMapping/locationMappingEditor/LocationMappingEditorTypes';

import FileUploaded from './FileUploaded';

const StepUpload: LocationMappingEditorFormStep<{
	inputFileName: LocationMappingEditorFormState['inputFileName'];
	setInputFileName: (
		files: LocationMappingEditorFormState['inputFileName']
	) => void;
	mappedFields: LocationMappingEditorFormState['mappedFields'];
	setMappedFields: (
		files: LocationMappingEditorFormState['mappedFields']
	) => void;
}> = ({
	onContinue,
	inputFileName,
	setInputFileName,
	mappedFields,
	setMappedFields,
}) => {
	const [files, setFiles] = useState<SovosUploadFile[]>(() => {
		if (inputFileName) {
			const file = new File([inputFileName], inputFileName) as SovosUploadFile;

			file.progress = { loading: false, percent: 100 };
			return [file];
		}

		return [];
	});

	const uploadFileMutation = useMutation(uploadFile, {
		onMutate: () => {
			setShowErrors(false);
		},
		onSuccess: (data) => {
			setMappedFields(
				data.header.map((header) => ({
					sourceField: header,
					targetField: null,
				}))
			);
			setFiles((files) => {
				const currentFile = files?.[0];

				if (currentFile) {
					const updatedFile = new File(
						[currentFile],
						currentFile.name
					) as SovosUploadFile;

					updatedFile.progress = { loading: false, percent: 100 };

					return [updatedFile];
				}

				return files;
			});
		},
		onError: () => {
			setFiles((files) => {
				const currentFile = files?.[0];

				const updatedFile = new File(
					[currentFile],
					currentFile.name
				) as SovosUploadFile;

				updatedFile.progress = { loading: false, percent: 100 };
				return [updatedFile];
			});
			setShowErrors(true);
		},
	});

	const [showErrors, setShowErrors] = useState(false);

	const validations = [
		{
			isValid:
				(inputFileName && mappedFields?.length && mappedFields.length > 0) ||
				inputFileName,
			message: 'Please upload your location headers',
		},
		{
			isValid: mappedFields !== undefined || !inputFileName,
			message: 'Invalid File. Please delete the file and check the content',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	const { locationMappingID } = useParams();

	return locationMappingID ? (
		<FileUploaded inputFileName={inputFileName!} onContinue={handleContinue} />
	) : (
		<FormStepLayout
			title='Upload location headers'
			subTitle={
				<>
					Upload an excel file containing your location headers. Need help?{' '}
					<SovosLink
						href='https://sovos.com/support'
						target='_blank'
						rel='noopener noreferrer'
					>
						Go to Sovos Help Center
					</SovosLink>
				</>
			}
			errors={errors}
			showErrors={showErrors}
			isLoading={uploadFileMutation.isLoading}
			onContinue={handleContinue}
		>
			<SovosFileUpload
				multiple={false}
				acceptedTypes={['xls', 'xlsx']}
				fileList={files || []}
				onFileDelete={() => {
					setShowErrors(false);
					setFiles([]);
					setInputFileName(undefined);
					setMappedFields(undefined);
				}}
				onFilesSelected={(selectedFiles: SovosUploadFile[]) => {
					if (selectedFiles.length > 0) {
						uploadFileMutation.mutate(selectedFiles);
						setFiles(
							selectedFiles.map((file) => {
								file.progress = { loading: true, percent: null };
								return file;
							})
						);
						setInputFileName(selectedFiles[0].name);
					}
				}}
				sx={{
					my: 3,
					borderColor:
						showErrors && errors?.length > 0 ? 'error.main' : undefined,
				}}
			/>
		</FormStepLayout>
	);
};

export default StepUpload;
